import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import OfficeStatusDetailsForm from "../forms/OfficeStatusDetailsForm";
import Timer from "components/Timer";
import useOfficeStatusDetails from "../hooks/useOfficeStatusDetails";
import useDeleteOfficeStatusDetails from "../hooks/useDeleteOfficeStatusDetails";
import {
  addStateOfficeStatusDetails,
  editStateOfficeStatusDetails,
  officeStatusDetailsFetching,
  officeStatusDetailsFilterText,
  officeStatusDetailsReFetching,
  rowEditOfficeStatusDetails,
  selectedRowsStateOfficeStatusDetails,
} from "../atoms/atoms";
import { IOfficeStatusDetails, IOfficeStatusDetailsTableData } from "../types";
import TableFilters from "components/TableFilters";

export const OfficeStatusDetailsTable: React.FC = () => {
  const { data: directData, refetch: reFetch } = useOfficeStatusDetails();
  const BulkDelete = useDeleteOfficeStatusDetails();

  // Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateOfficeStatusDetails
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeStatusDetails
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOfficeStatusDetails
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    officeStatusDetailsFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOfficeStatusDetails);
  const userPermissions = useRecoilValue(permissionsSelector);

  const [isFetching, setIsFetching] = useRecoilState(
    officeStatusDetailsFetching
  );
  const isReFetching = useRecoilValue(officeStatusDetailsReFetching);

  // Local State
  const [tableData, setTableData] = useState<IOfficeStatusDetails[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    IOfficeStatusDetails[]
  >([]);

  // Permisision States
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatusdetails_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatusdetails_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatusdetails_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IOfficeStatusDetails[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].officeStatusDetailsName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  // For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.officeStatusDetailsName &&
        item.officeStatusDetailsName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );

  useEffect(() => {
    if (!recent) setTableFilteredData(filtereditems);
  }, [filterTextState]);

  // Handle Functions
  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IOfficeStatusDetailsTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IOfficeStatusDetailsTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids =
      selectedRows && selectedRows.map((row) => row.officeStatusDetailsId);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {`Are you sure you want to deactivate ${rowsCount} ${
        rowsCount > 1 ? "Office Status Details" : "Office Status Detail"
      }`}
    </div>
  );

  const matColumns = [
    {
      title: "Office Status Details Name",
      field: "officeStatusDetailsName",
      customSort: (a, b) =>
        a.officeStatusDetailsName && b.officeStatusDetailsName
          ? a.officeStatusDetailsName
              .trim()
              .localeCompare(b.officeStatusDetailsName.trim())
          : a.officeStatusDetailsName
          ? 0
          : -1,
    },
    {
      title: "Office Status",
      field: "status",
      customSort: (a, b) =>
        a.status && b.status
          ? a.status.trim().localeCompare(b.status.trim())
          : a.status
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedby",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },

    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <OfficeStatusDetailsForm setRecent={setRecent} />
        </div>
      ) : (
        <div className="">
          <div className="flex w-full">
            <div className="bg-white w-full">
              <div className="flex justify-between text-xs items-center w-full">
                <div className="">
                  <TableFilters
                    defaultText={filterTextState}
                    recentText={recent}
                    setRecent={setRecent}
                    textInputName={"OfficeStatusDetailsName"}
                    columnDetails={"Office Status Details Name"}
                    filterText={setFilterTextState}
                    disabledButton={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                    disabledTextBox={
                      selectedRows !== undefined && selectedRows.length > 0
                    }
                  />
                </div>
                <div className="">
                  {userPermissions.includes(addPermission) ? (
                    <AddButton
                      buttonText="New Office Status Details"
                      onClick={() => handleAdd()}
                      disabled={
                        actionEditState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
              <div className="">
                <MaterialTableTS
                  data={
                    isFetching
                      ? []
                      : filterTextState === "" && recent === ""
                      ? tableData
                      : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
                  }
                  showTitle={true}
                  columns={matColumns}
                  isLoading={isFetching}
                  onSelectionChange={handleSelectedRowsChange}
                  selectedRows={selectedRows}
                  selection={
                    !actionEditState &&
                    !actionAddState &&
                    userPermissions.includes(deletePermission)
                  }
                  setConfirmModalOpen={setOpenModalDelete}
                />
              </div>
            </div>
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Office Status Details"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </div>
      )}
    </div>
  );
};
export default OfficeStatusDetailsTable;
